/**
 * Checkout Success Page
 * Displays confirmation after successful payment
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/hooks/useSubscription';
import { PaymentSuccess } from '@/components/feature/PaymentSuccess';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export function CheckoutSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const { currentSubscription, loadCurrentSubscription, plans } = useSubscription();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const paymentIntentId = searchParams.get('payment_intent');
  const planCode = searchParams.get('plan');
  const amount = searchParams.get('amount');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth?mode=login&redirect=/checkout/success');
      return;
    }

    // Load current subscription to display details
    const loadData = async () => {
      try {
        setLoading(true);
        await loadCurrentSubscription();
        setError(null);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load subscription';
        setError(errorMessage);
        console.error('Error loading subscription:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [isAuthenticated, loadCurrentSubscription, navigate]);

  const selectedPlan = planCode
    ? plans.find((p) => p.code === planCode)
    : null;

  const planName = selectedPlan?.name || currentSubscription?.plan_detail.name || 'Seu Plano';
  const planAmount = amount ? parseInt(amount) : currentSubscription?.price_paid_cents || 0;

  return (
    <div className="min-h-screen bg-[#0A0A0F]">
      <Navbar />

      <div className="pt-32 pb-20 px-6 md:px-10">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1
              className="text-4xl md:text-5xl font-light text-white mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Sua Assinatura
            </h1>
          </div>

          {/* Content */}
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C8A96E]" />
            </div>
          ) : error ? (
            <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-8 text-center space-y-4">
              <h3 className="text-lg font-semibold text-red-300">Erro ao Carregar Detalhes</h3>
              <p className="text-red-300/70">{error}</p>
              <button
                onClick={() => navigate('/subscription-dashboard')}
                className="px-6 py-2 bg-red-500/20 hover:bg-red-500/30 border border-red-500/50 text-red-300 rounded-lg transition-colors"
              >
                Ir para Assinatura
              </button>
            </div>
          ) : (
            <PaymentSuccess
              subscription={currentSubscription || undefined}
              planName={planName}
              amount={planAmount}
            />
          )}

          {/* Debug Info (only in development) */}
          {import.meta.env.DEV && paymentIntentId && (
            <div className="mt-8 p-4 bg-white/5 border border-white/10 rounded-lg text-center">
              <p className="text-xs text-white/30">
                Payment Intent ID: {paymentIntentId}
              </p>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
